import clsx from 'clsx'
import { useFlags } from 'launchdarkly-react-client-sdk'
import React, { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { match } from 'ts-pattern'

import Spinner from '@/components-latest/primitives/Spinner'
import Text from '@/components-latest/primitives/Text'
import OrderDetails from '@/components-latest/ui/OrdersPage/OrderDetails'
import { useLazyFetchOrdersListQuery } from '@/ducks/orders/services/orders.api'
import { resetOrdersStateToInitial } from '@/ducks/orders/slices/orders.slice'
import { selectStoreId } from '@/ducks/store/selectors/store.selectors'

import Styles from './ReadyOrders.module.scss'

const ReadyOrders = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { lmd_store_web_restaurant_chains_feature } = useFlags()

  const storeId = useSelector(selectStoreId)

  const [fetchOrders, { data: _orders, isFetching: isOrdersLoading }] =
    useLazyFetchOrdersListQuery()

  const orders = _orders?.orders
  const shouldBeFaded = orders && orders.length > 0 && isOrdersLoading

  const getOrders = useCallback(() => {
    if (!storeId) return
    fetchOrders({ status: 'READY', store_id: storeId })
  }, [fetchOrders, storeId])

  useEffect(() => {
    getOrders()
  }, [getOrders, storeId])

  useEffect(() => {
    return () => {
      dispatch(resetOrdersStateToInitial())
    }
  }, [dispatch])

  return (
    <div
      className={clsx(Styles.readyOrders, { [Styles.faded]: shouldBeFaded })}>
      {match({ orders, isOrdersLoading })
        .with(
          { isOrdersLoading: true },
          ({ orders }) => !orders || orders.length === 0,
          () => (
            <div className={Styles.message}>
              <Spinner />
            </div>
          )
        )
        .when(
          ({ orders }) => orders && orders.length === 0,
          () => (
            <div className={Styles.message}>
              <img
                className={Styles.imageForEmpty}
                src="/images/emptyOrders.svg"
                alt="Waiting for ready orders"></img>
              <Text variant="title-3" gutter={8}>
                {t('orders.empty-title')}
              </Text>
              <Text variant="subtitle" className={Styles.dimmed}>
                {t('orders.empty-message')}
              </Text>
            </div>
          )
        )
        .when(
          ({ orders }) => orders && orders.length > 0,
          ({ orders }) =>
            orders!.map(order => (
              <OrderDetails
                key={order.id}
                order={order}
                actions={['return-to-stock']}
                showBranch={lmd_store_web_restaurant_chains_feature}
              />
            ))
        )
        .otherwise(() => null)}
    </div>
  )
}

export default ReadyOrders
